import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { KARMANGALIN_THATHUVANGAL_BOOK } from '../data/karmangalinThathuvangalData';
import SEO from '../SEO';

const KarmangalinThathuvangalPage = () => {
  const { chapterSlug } = useParams();
  const navigate = useNavigate();
  const mainContentRef = useRef(null);

  const [selectedCategory, setSelectedCategory] = useState('அனைத்தும்');
  const [searchTerm, setSearchTerm] = useState('');
  const [fontSize, setFontSize] = useState(window.innerWidth < 768 ? 8 : 15);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [showSharePopup, setShowSharePopup] = useState(false);

  // Track window resize for mobile/desktop switching
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive font sizing based on screen width
  // ≤400px: body 8px, subhead 12px, head 14px
  // >400px mobile: body 12px, subhead 14px, head 18px
  // Desktop: body 14px, subhead 18px, head 24px
  const screenW = typeof window !== 'undefined' ? window.innerWidth : 400;
  const isCompact = screenW <= 400;
  const bodyFontSize = isCompact ? '8px' : (isMobile ? '12px' : '14px');
  const headFontSize = isCompact ? '14px' : (isMobile ? '18px' : '2.1rem');
  const subHeadFontSize = isCompact ? '12px' : (isMobile ? '14px' : '1.2rem');
  const bannerTitleSize = isCompact ? '12px' : (isMobile ? '16px' : '2.4rem');
  const bannerSubSize = isCompact ? '9px' : (isMobile ? '12px' : '1.15rem');
  const badgeSize = isCompact ? '7px' : (isMobile ? '10px' : '0.8rem');
  const searchFontSize = isCompact ? '8px' : (isMobile ? '11px' : '0.95rem');
  const maxSliderFont = isMobile ? 14 : 24;

  // Find active chapter based on URL or default to chapter 20 or 1
  const activeChapter = KARMANGALIN_THATHUVANGAL_BOOK.chapters.find(
    (ch) => ch.slug === chapterSlug
  ) || KARMANGALIN_THATHUVANGAL_BOOK.chapters.find((ch) => ch.id === 20) || KARMANGALIN_THATHUVANGAL_BOOK.chapters[0];

  // Scroll to content when chapter changes
  useEffect(() => {
    if (chapterSlug) {
      setTimeout(() => {
        mainContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [chapterSlug, activeChapter.id]);

  const handleChapterSelect = (slug) => {
    navigate(`/karmangalin-thathuvangal/${slug}`);
    setSidebarOpen(false);
    setTimeout(() => {
      mainContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const filteredChapters = KARMANGALIN_THATHUVANGAL_BOOK.chapters.filter((ch) => {
    const matchesCategory = selectedCategory === 'அனைத்தும்' || ch.category === selectedCategory;
    const matchesSearch =
      ch.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ch.subtitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ch.category.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const currentIndex = KARMANGALIN_THATHUVANGAL_BOOK.chapters.findIndex((ch) => ch.id === activeChapter.id);
  const prevChapter = currentIndex > 0 ? KARMANGALIN_THATHUVANGAL_BOOK.chapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < KARMANGALIN_THATHUVANGAL_BOOK.chapters.length - 1 ? KARMANGALIN_THATHUVANGAL_BOOK.chapters[currentIndex + 1] : null;

  // Share functionality
  const getShareUrl = () => `https://ahobilam.com/karmangalin-thathuvangal/${activeChapter.slug}`;
  const getShareText = () => `📖 ${activeChapter.title}\n✨ ${activeChapter.subtitle}\n\n🙏 வைதீக கர்மங்களின் தத்துவங்கள் - அபூர்வ நூல்\n\n👉 படிக்க:`;

  const handleShare = async () => {
    const url = getShareUrl();
    const text = getShareText();
    // Try native Web Share API first (works great on mobile)
    if (navigator.share) {
      try {
        await navigator.share({ title: activeChapter.title, text, url });
        return;
      } catch (e) { /* user cancelled or not supported */ }
    }
    // Fallback: show share popup
    setShowSharePopup(true);
  };

  const shareToWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(getShareText() + ' ' + getShareUrl())}`, '_blank');
    setShowSharePopup(false);
  };
  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}&quote=${encodeURIComponent(getShareText())}`, '_blank');
    setShowSharePopup(false);
  };
  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}&url=${encodeURIComponent(getShareUrl())}`, '_blank');
    setShowSharePopup(false);
  };
  const copyLink = () => {
    navigator.clipboard.writeText(getShareUrl()).then(() => {
      alert('✅ இணைப்பு நகலெடுக்கப்பட்டது!');
      setShowSharePopup(false);
    });
  };

  return (
    <div style={{ backgroundColor: '#FAF6EE', minHeight: '100vh', color: '#2C1D11', fontFamily: "'Noto Sans Tamil', 'Mukta Malar', 'Segoe UI', Arial, sans-serif", fontSize: bodyFontSize }}>
      <SEO
        title={`${activeChapter.title} - ${KARMANGALIN_THATHUVANGAL_BOOK.title}`}
        description={`${activeChapter.subtitle} - வைதீக கர்மங்களின் தத்துவங்கள் மற்றும் சடங்கு முறைகள். அபூர்வ வைதீக கருவூலம் - ahobilam.com`}
        image="https://ahobilam.com/images/karmangalin-share-banner.png"
        type="article"
      />

      {/* Main Header Banner */}
      <header
        style={{
          background: 'linear-gradient(135deg, #1A237E 0%, #283593 50%, #311B92 100%)',
          color: '#FAF6EE',
          padding: isMobile ? '1rem 0.8rem' : '2.5rem 1.5rem',
          textAlign: 'center',
          boxShadow: '0 4px 15px rgba(26, 35, 126, 0.15)',
          borderBottom: '3px solid #CC6600'
        }}
      >
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <span
            style={{
              backgroundColor: '#CC6600',
              color: '#FFF8E1',
              padding: isMobile ? '2px 10px' : '4px 14px',
              borderRadius: '20px',
              fontSize: isCompact ? '7px' : (isMobile ? '10px' : '0.85rem'),
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            அபூர்வ வைதீக கருவூலம்
          </span>
          <h1 style={{ fontSize: bannerTitleSize, margin: isMobile ? '0.4rem 0 0.2rem' : '0.8rem 0 0.4rem', color: '#FFD54F', fontWeight: 'bold', textShadow: '1px 1px 3px rgba(0,0,0,0.3)' }}>
            📖 {KARMANGALIN_THATHUVANGAL_BOOK.title}
          </h1>
          <p style={{ fontSize: bannerSubSize, color: '#E8EAF6', maxWidth: '800px', margin: isMobile ? '0 auto 0.6rem' : '0 auto 1.2rem', lineHeight: '1.5' }}>
            {KARMANGALIN_THATHUVANGAL_BOOK.subtitle}
          </p>

          {/* Search Bar */}
          <div style={{ maxWidth: '550px', margin: '0 auto', display: 'flex', gap: '10px' }}>
            <input
              type="text"
              placeholder="🔍 தேடுக (எ.கா: விவாகம், உபநயனம்)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: isMobile ? '8px 12px' : '12px 18px',
                borderRadius: '25px',
                border: '2px solid #D4AF37',
                backgroundColor: '#FDF8ED',
                color: '#1A237E',
                fontSize: searchFontSize,
                outline: 'none'
              }}
            />
          </div>
        </div>
      </header>

      {/* Mobile Floating Toggle Button */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen((prev) => !prev)}
          style={{
            position: 'fixed',
            bottom: '90px',
            left: '12px',
            zIndex: 1100,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: '2px solid #D4AF37',
            backgroundColor: sidebarOpen ? '#C62828' : '#1A237E',
            color: '#FFF8E1',
            fontSize: '1.3rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(26, 35, 126, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s ease'
          }}
          title={sidebarOpen ? 'பொருளடக்கத்தை மூடு' : 'பொருளடக்கத்தைக் காட்டு'}
        >
          {sidebarOpen ? '✕' : '📚'}
        </button>
      )}

      {/* Mobile Overlay Backdrop */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(26, 35, 126, 0.5)',
            zIndex: 1000,
            transition: 'opacity 0.2s ease'
          }}
        />
      )}

      {/* Main Container */}
      <div style={{ maxWidth: '1280px', margin: '2rem auto', padding: '0 1rem', display: 'flex', gap: '2rem' }}>
        
        {/* Left Sidebar */}
        <aside
          style={{
            ...(isMobile ? {
              position: 'fixed',
              top: 0,
              left: sidebarOpen ? 0 : '-100%',
              width: '85vw',
              maxWidth: '340px',
              height: '100vh',
              zIndex: 1050,
              transition: 'left 0.3s ease',
              overflowY: 'auto',
              borderRadius: '0 12px 12px 0'
            } : {
              width: '320px',
              flexShrink: 0,
              maxHeight: 'calc(100vh - 120px)',
              position: 'sticky',
              top: '80px',
              overflowY: 'auto',
              borderRadius: '12px'
            }),
            backgroundColor: '#FDF8ED',
            padding: '1.2rem',
            boxShadow: '0 2px 10px rgba(74, 20, 140, 0.08)',
            border: '1px solid #E65100'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '2px solid #E65100', paddingBottom: '0.6rem' }}>
            <h2 style={{ fontSize: '1.2rem', color: '#1A237E', margin: 0, fontWeight: 'bold' }}>
              📚 பொருளடக்கம் (28 தலைப்புகள்)
            </h2>
            <span style={{ backgroundColor: '#1B5E20', color: '#FFF', fontSize: '0.75rem', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>
              28 / 28
            </span>
          </div>

          {/* Category Filter */}
          <div style={{ marginBottom: '1rem' }}>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #CC6600',
                backgroundColor: '#FAF6EE',
                color: '#1A237E',
                fontWeight: 'bold',
                fontSize: '0.88rem'
              }}
            >
              <option value="அனைத்தும்">அனைத்துப் பிரிவுகள் (28 தலைப்புகள்)</option>
              {KARMANGALIN_THATHUVANGAL_BOOK.categories.map((cat, idx) => (
                <option key={idx} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Chapter List */}
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {filteredChapters.map((chapter) => {
              const isActive = chapter.id === activeChapter.id;
              return (
                <li key={chapter.id} style={{ marginBottom: '6px' }}>
                  <button
                    onClick={() => handleChapterSelect(chapter.slug)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: isActive ? '2px solid #CC6600' : '1px solid #E5D5B5',
                      backgroundColor: isActive ? '#FFF3E0' : '#FAF6EE',
                      color: isActive ? '#C62828' : '#1A237E',
                      fontWeight: isActive ? 'bold' : 'normal',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span style={{ fontSize: '0.92rem', lineHeight: '1.4' }}>{chapter.title}</span>
                    {isActive && <span style={{ color: '#CC6600', fontSize: '1.1rem' }}>▶</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Main Content Area */}
        <main ref={mainContentRef} style={{ flex: 1, minWidth: 0, scrollMarginTop: '90px' }}>
          {/* Controls Bar — compact 3-row, 8px on mobile */}
          <div
            style={{
              backgroundColor: '#FDF8ED',
              padding: isMobile ? '4px 8px' : '0.35rem 0.8rem',
              borderRadius: '8px',
              border: '1px solid #E5D5B5',
              marginBottom: '0.5rem',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: isMobile ? '3px 8px' : '6px 12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
              lineHeight: isMobile ? '1.3' : '1.5',
              fontSize: isMobile ? '8px' : '0.78rem'
            }}
          >
            <span style={{ color: '#1B5E20', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
              📌 {activeChapter.id}/25
            </span>
            <span style={{ color: '#D4AF37' }}>|</span>
            <span style={{ color: '#4A148C', fontWeight: 'bold', whiteSpace: 'nowrap' }}>எழுத்து:</span>
            <button
              onClick={() => setFontSize((prev) => Math.max(prev - 1, 8))}
              style={{ padding: '0px 5px', borderRadius: '3px', border: '1px solid #CC6600', backgroundColor: '#FAF6EE', cursor: 'pointer', fontWeight: 'bold', fontSize: 'inherit', color: '#1A237E', lineHeight: '1.4' }}
            >A-</button>
            <span style={{ fontWeight: 'bold', color: '#CC6600', minWidth: '16px', textAlign: 'center' }}>{fontSize}</span>
            <button
              onClick={() => setFontSize((prev) => Math.min(prev + 1, maxSliderFont))}
              style={{ padding: '0px 5px', borderRadius: '3px', border: '1px solid #CC6600', backgroundColor: '#FAF6EE', cursor: 'pointer', fontWeight: 'bold', fontSize: 'inherit', color: '#1A237E', lineHeight: '1.4' }}
            >A+</button>
            <span style={{ color: '#D4AF37' }}>|</span>
            <button
              onClick={handleShare}
              style={{ padding: '0px 6px', borderRadius: '3px', border: '1px solid #1B5E20', backgroundColor: '#E8F5E9', cursor: 'pointer', fontWeight: 'bold', fontSize: 'inherit', color: '#1B5E20', whiteSpace: 'nowrap', lineHeight: '1.4' }}
            >📤 பகிர்</button>
          </div>

          {/* Share Popup */}
          {showSharePopup && (
            <>
              <div
                onClick={() => setShowSharePopup(false)}
                style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: 'rgba(26, 35, 126, 0.4)', zIndex: 2000
                }}
              />
              <div
                style={{
                  position: 'fixed',
                  bottom: isMobile ? '0' : '50%',
                  left: isMobile ? '0' : '50%',
                  right: isMobile ? '0' : 'auto',
                  transform: isMobile ? 'none' : 'translate(-50%, 50%)',
                  backgroundColor: '#FDF8ED',
                  borderRadius: isMobile ? '16px 16px 0 0' : '16px',
                  padding: '1.2rem',
                  zIndex: 2001,
                  boxShadow: '0 -4px 20px rgba(26, 35, 126, 0.15)',
                  border: '2px solid #D4AF37',
                  maxWidth: '400px',
                  width: isMobile ? '100%' : '380px'
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.8rem', borderBottom: '1px solid #E5D5B5', paddingBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 'bold', color: '#1A237E', fontSize: '0.85rem' }}>📤 இந்தப் பக்கத்தைப் பகிரவும்</span>
                  <button onClick={() => setShowSharePopup(false)} style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#C62828' }}>✕</button>
                </div>

                {/* Preview */}
                <div style={{ backgroundColor: '#FAF6EE', borderRadius: '8px', padding: '0.6rem', marginBottom: '0.8rem', border: '1px solid #E5D5B5' }}>
                  <div style={{ fontSize: '0.75rem', color: '#CC6600', fontWeight: 'bold' }}>📖 {activeChapter.title}</div>
                  <div style={{ fontSize: '0.65rem', color: '#1B5E20', marginTop: '2px' }}>{activeChapter.subtitle}</div>
                  <div style={{ fontSize: '0.6rem', color: '#4A148C', marginTop: '4px', wordBreak: 'break-all' }}>{getShareUrl()}</div>
                </div>

                {/* Share Buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <button onClick={shareToWhatsApp} style={{ padding: '10px', borderRadius: '10px', border: '1px solid #1B5E20', backgroundColor: '#E8F5E9', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.78rem', color: '#1B5E20', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    💬 WhatsApp
                  </button>
                  <button onClick={shareToFacebook} style={{ padding: '10px', borderRadius: '10px', border: '1px solid #1A237E', backgroundColor: '#E8EAF6', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.78rem', color: '#1A237E', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    📘 Facebook
                  </button>
                  <button onClick={shareToTwitter} style={{ padding: '10px', borderRadius: '10px', border: '1px solid #283593', backgroundColor: '#E3F2FD', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.78rem', color: '#283593', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    🐦 Twitter/X
                  </button>
                  <button onClick={copyLink} style={{ padding: '10px', borderRadius: '10px', border: '1px solid #CC6600', backgroundColor: '#FFF3E0', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.78rem', color: '#CC6600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                    🔗 நகலெடு
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Reading Card */}
          <article
            style={{
              backgroundColor: '#FDF8ED',
              borderRadius: isMobile ? '8px' : '12px',
              padding: isMobile ? '0.8rem 0.9rem' : '2rem 2.2rem',
              boxShadow: '0 3px 15px rgba(26, 35, 126, 0.06)',
              border: '1px solid #E65100',
              minHeight: isMobile ? '300px' : '450px'
            }}
          >
            {/* Category Tag */}
            <div style={{ marginBottom: '0.8rem' }}>
              <span
                style={{
                  backgroundColor: '#1A237E',
                  color: '#FFF8E1',
                  padding: isMobile ? '2px 8px' : '4px 12px',
                  borderRadius: '15px',
                  fontSize: badgeSize,
                  fontWeight: 'bold'
                }}
              >
                {activeChapter.category}
              </span>
            </div>

            {/* Title */}
            <h1 style={{ fontSize: headFontSize, color: '#C62828', marginTop: 0, marginBottom: '0.3rem', borderBottom: '2px dashed #D4AF37', paddingBottom: isMobile ? '0.4rem' : '0.8rem' }}>
              {activeChapter.title}
            </h1>
            <h3 style={{ fontSize: subHeadFontSize, color: '#1B5E20', marginTop: '0.3rem', marginBottom: isMobile ? '0.8rem' : '1.8rem', fontStyle: 'italic' }}>
              {activeChapter.subtitle}
            </h3>

            {/* Chapter Content */}
            {activeChapter.content ? (
              <div style={{ fontSize: `${fontSize}px`, lineHeight: '1.85', color: '#2C1D11' }}>
                {activeChapter.content.split('[SPECIAL_GREEN_BOX]').map((chunk, index) => {
                  if (index === 0) {
                    return (
                      <div key={index} style={{ whiteSpace: 'pre-line', marginBottom: '1.5rem' }}>
                        {chunk}
                      </div>
                    );
                  }

                  const parts = chunk.split('[/SPECIAL_GREEN_BOX]');
                  const greenBoxContent = parts[0];
                  const remainingText = parts[1] || '';

                  return (
                    <React.Fragment key={index}>
                      {/* Favorite Light Sacred Green Background Box */}
                      <div
                        style={{
                          backgroundColor: '#E8F5E9',
                          color: '#1B5E20',
                          border: '2px solid #2D5A1E',
                          borderRadius: '12px',
                          padding: '1.6rem 1.8rem',
                          margin: '1.8rem 0',
                          boxShadow: '0 4px 15px rgba(27, 94, 32, 0.12)',
                          lineHeight: '1.8',
                          whiteSpace: 'pre-line',
                          fontWeight: '500'
                        }}
                      >
                        {greenBoxContent.trim()}
                      </div>

                      {remainingText && (
                        <div style={{ whiteSpace: 'pre-line', marginBottom: '1.5rem' }}>
                          {remainingText}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem 1rem', backgroundColor: '#FAF6EE', borderRadius: '8px', border: '1px dashed #CC6600' }}>
                <p style={{ fontSize: '1.2rem', color: '#CC6600', fontWeight: 'bold', margin: '0 0 0.5rem' }}>
                  ⏳ இந்தப் பகுதியின் முழுமையான உரைகள் டிஜிட்டல் மயமாக்கப்பட்டு வருகிறது...
                </p>
              </div>
            )}

            {/* Bottom Chapter Navigation Buttons (Compact single line format) */}
            <div
              style={{
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px dashed #E5D5B5',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {prevChapter ? (
                <button
                  onClick={() => handleChapterSelect(prevChapter.slug)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '15px',
                    border: '1px solid #1A237E',
                    backgroundColor: '#1A237E',
                    color: '#FAF6EE',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: isMobile ? '10px' : '0.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  title={prevChapter.title}
                >
                  ◀ முன் தலைப்பு
                </button>
              ) : <div />}

              {nextChapter && (
                <button
                  onClick={() => handleChapterSelect(nextChapter.slug)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '15px',
                    border: '1px solid #CC6600',
                    backgroundColor: '#CC6600',
                    color: '#FAF6EE',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    fontSize: isMobile ? '10px' : '0.8rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  title={nextChapter.title}
                >
                  அடுத்தது ▶
                </button>
              )}
            </div>
          </article>
        </main>
      </div>
    </div>
  );
};

export default KarmangalinThathuvangalPage;
